import React, {useEffect, useState, useCallback, useRef} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  Image,
  useWindowDimensions,
  Modal,
  Pressable,
  Platform,
  AppState,
  AppStateStatus,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {settingsService, photoService, photosPickerService} from '../services';
import {PhotoProcessedEvent} from '../services/photoService';
import {openOAuthUrl, openBrowserUrl, closeBrowser} from '../services/oauthBrowser';
import {clearFrameCache} from '../services/offlineCache';
import {ConfirmSelectionResult} from '../services/photosPickerService';
import {Photo} from '../types';
import {useSettingsNavigation} from '../hooks/useSettingsNavigation';
import {useRouting} from '../hooks/useRouting';
import {useAppDispatch} from '../store';
import {logout} from '../store/authSlice';
import {
  SettingsLayout,
  ConnectServiceCard,
  BodyText,
  theme,
} from '../components/DarkTheme';
import OfflineBanner from '../components/OfflineBanner';
import {useDataLoader} from '../hooks/useDataLoader';

interface OAuthWindow {
  location: { href: string };
  close: () => void;
}

declare const window: {
  location: { href: string; pathname: string };
  history: { replaceState: (data: any, title: string, url: string) => void };
  open: (url: string, target?: string) => OAuthWindow | null;
};

export default function GallerySettingsScreen() {
  const {t} = useTranslation();
  const {navigateToSettingsScreen} = useSettingsNavigation('GallerySettings');
  const dispatch = useAppDispatch();
  const {width} = useWindowDimensions();
  const isWide = width >= 768;
  const {getSearchParam, clearSearchParams} = useRouting();

  // Google Photos state
  const [isPhotosConnected, setIsPhotosConnected] = useState(false);
  const [photosEmail, setPhotosEmail] = useState<string | null>(null);
  const [pickerStatus, setPickerStatus] = useState<'idle' | 'waiting' | 'complete' | 'error' | 'popup_blocked'>('idle');

  // Current photos
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [maxPhotos, setMaxPhotos] = useState<number | null>(null);
  const [savingProgress, setSavingProgress] = useState<{current: number; total: number} | null>(null);
  const [skeletonCount, setSkeletonCount] = useState(0);
  const [processingTotal, setProcessingTotal] = useState(0);
  const sseCleanupRef = useRef<(() => void) | null>(null);
  const processingPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const photoUrlsRef = useRef<Record<string, string>>({});

  // Account menu
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  // Limit warning modal
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [limitWarningInfo, setLimitWarningInfo] = useState<{added: number; skipped: number; max: number} | null>(null);

  // Delete confirmation
  const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Track if we're waiting for OAuth to complete (native only)
  const isWaitingForOAuth = useRef(false);
  const appState = useRef(AppState.currentState);

  const handleLogout = async () => {
    await dispatch(logout());
  };

  // Load thumbnail URL for a single photo
  const loadPhotoUrl = useCallback(async (photoId: string) => {
    try {
      const url = await photoService.getThumbnailUrl(photoId);
      photoUrlsRef.current = {...photoUrlsRef.current, [photoId]: url};
      setPhotoUrls(prev => ({...prev, [photoId]: url}));
    } catch (error) {
      console.error(`Failed to get URL for photo ${photoId}`, error);
    }
  }, []);

  // Load thumbnail URLs for photos (for gallery grid display)
  const loadPhotoUrls = useCallback(async (photosToLoad: Photo[]) => {
    const urls: Record<string, string> = {};
    await Promise.all(
      photosToLoad.filter(p => p.hasS3Cache || p.hasThumbnail).map(async (photo) => {
        try {
          // Use thumbnail if available, otherwise fall back to full-size
          const url = await photoService.getThumbnailUrl(photo.id);
          urls[photo.id] = url;
        } catch (error) {
          console.error(`Failed to get URL for photo ${photo.id}`, error);
        }
      })
    );
    photoUrlsRef.current = {...photoUrlsRef.current, ...urls};
    setPhotoUrls(prev => ({...prev, ...urls}));
  }, []);

  // Handle SSE photo processed event — update photo in state and load its URL
  const handlePhotoProcessed = useCallback((event: PhotoProcessedEvent) => {
    setPhotos(prev => prev.map(p =>
      p.id === event.photoId
        ? {...p, hasS3Cache: true, hasThumbnail: event.hasThumbnail}
        : p
    ));
    if (event.hasThumbnail) {
      loadPhotoUrl(event.photoId);
    }
  }, [loadPhotoUrl]);

  // Handle SSE processing complete — no-op, the confirm flow handles final state
  const handleProcessingComplete = useCallback(() => {}, []);

  // Poll for photos still being processed (S3 upload not finished yet)
  const startProcessingPoll = useCallback(() => {
    if (processingPollRef.current) return;

    processingPollRef.current = setInterval(async () => {
      try {
        const {photos: freshPhotos, maxPhotos: freshMax} = await photoService.getSelectedPhotos();

        // Only update state if processing status actually changed
        setPhotos(prev => {
          const changed = freshPhotos.some(fp => {
            const old = prev.find(p => p.id === fp.id);
            return !old || old.hasS3Cache !== fp.hasS3Cache || old.hasThumbnail !== fp.hasThumbnail;
          }) || prev.length !== freshPhotos.length;
          return changed ? freshPhotos : prev;
        });
        setMaxPhotos(freshMax);

        // Load URLs only for newly ready photos that we don't have yet
        const newlyReady = freshPhotos.filter(p =>
          (p.hasS3Cache || p.hasThumbnail) && !photoUrlsRef.current[p.id]
        );
        if (newlyReady.length > 0) {
          await loadPhotoUrls(newlyReady);
        }

        // Stop polling once all photos have S3 cache
        const unprocessed = freshPhotos.filter(p => !p.hasS3Cache);
        if (unprocessed.length === 0) {
          setProcessingTotal(0);
          if (processingPollRef.current) {
            clearInterval(processingPollRef.current);
            processingPollRef.current = null;
          }
        }
      } catch (e) {
        console.error('Processing poll failed:', e);
      }
    }, 3000);
  }, [loadPhotoUrls]);

  // Cleanup SSE and polling on unmount
  useEffect(() => {
    return () => {
      sseCleanupRef.current?.();
      if (processingPollRef.current) {
        clearInterval(processingPollRef.current);
        processingPollRef.current = null;
      }
    };
  }, []);

  const loadInitialData = useCallback(async () => {
    const status = await settingsService.getOAuthStatus();
    setIsPhotosConnected(status.photosConnected);
    setPhotosEmail(status.photosEmail || null);

    // Load existing photos and maxPhotos config
    const {photos: photosData, maxPhotos: maxPhotosConfig} = await photoService.getSelectedPhotos();
    setPhotos(photosData);
    setMaxPhotos(maxPhotosConfig);

    if (photosData.length > 0) {
      await loadPhotoUrls(photosData);
    }

    // If any photos are still being processed, resume tracking and poll
    const unprocessedOnLoad = photosData.filter(p => !p.hasS3Cache).length;
    if (unprocessedOnLoad > 0) {
      setProcessingTotal(unprocessedOnLoad);
      startProcessingPoll();
    }
  }, [loadPhotoUrls, startProcessingPoll]);

  const {isLoading, loadError, isOnline, refreshControlProps, refresh} = useDataLoader(loadInitialData);

  // Refresh data when app returns to foreground after OAuth (native only)
  useEffect(() => {
    if (Platform.OS === 'web') return;

    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active' &&
        isWaitingForOAuth.current
      ) {
        // App came back to foreground after OAuth
        isWaitingForOAuth.current = false;
        refresh();
      }
      appState.current = nextAppState;
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);
    return () => subscription.remove();
  }, [refresh]);

  // Clean up OAuth query params from URL and refresh data
  useEffect(() => {
    const oauthSuccess = getSearchParam('oauth_success');
    const oauthError = getSearchParam('oauth_error');
    if (oauthSuccess || oauthError) {
      clearSearchParams();
      // Refresh data after OAuth redirect
      if (oauthSuccess) {
        refresh();
      }
    }
  }, [getSearchParam, clearSearchParams, refresh]);

  const handleConnectPhotos = async () => {
    try {
      const platform = Platform.OS === 'web' ? 'web' : 'native';
      const {authorizationUrl} = await settingsService.getPhotosAuthUrl(platform);

      if (Platform.OS === 'web') {
        // On web, redirect the page
        window.location.href = authorizationUrl;
      } else {
        // On native, open system browser - app will return via deep link
        isWaitingForOAuth.current = true;
        await openOAuthUrl(authorizationUrl);
        // Don't refresh here - will be triggered by AppState change or deep link
      }
    } catch (error) {
      console.error('Failed to get auth URL', error);
      isWaitingForOAuth.current = false;
    }
  };

  const handleDisconnect = async () => {
    try {
      await settingsService.disconnectPhotos();
      // Clear cached photos from local storage
      await clearFrameCache();
      setIsPhotosConnected(false);
      setPhotosEmail(null);
      setPhotos([]);
      setPhotoUrls({});
      setShowDisconnectConfirm(false);
      setShowAccountMenu(false);
    } catch (error) {
      console.error('Failed to disconnect', error);
    }
  };

  const handleOpenPhotoPicker = async () => {
    if (Platform.OS === 'web') {
      // Web: Open window immediately to preserve user gesture context (prevents popup blocker)
      const pickerWindow = window.open('about:blank', '_blank');
      if (!pickerWindow) {
        setPickerStatus('popup_blocked');
        return;
      }

      try {
        setPickerStatus('waiting');
        const session = await photosPickerService.createSession();

        // Navigate the already-open window to the picker URL
        // Add /autoclose to make Google close the window when done
        pickerWindow.location.href = session.pickerUri + '/autoclose';

        const pollInterval = photosPickerService.parsePollInterval(session.pollingConfig.pollInterval);

        // Poll for completion
        const poll = async () => {
          try {
            const status = await photosPickerService.getSessionStatus(session.sessionId);
            if (status.mediaItemsSet) {
              setPickerStatus('complete');
              setIsSaving(true);

              // Get the count of selected photos first
              let photoCount = 0;
              try {
                photoCount = await photosPickerService.getMediaItemsCount(session.sessionId);
                setSavingProgress({current: 0, total: photoCount});
                setSkeletonCount(photoCount);
              } catch (e) {
                console.warn('Failed to get media items count', e);
              }

              // Subscribe to SSE for real-time thumbnail updates
              try {
                const cleanup = await photoService.subscribeToProcessingEvents(
                  handlePhotoProcessed,
                  handleProcessingComplete,
                  (error) => {
                    console.error('SSE error:', error);
                    setSkeletonCount(0);
                    refresh();
                  }
                );
                sseCleanupRef.current = cleanup;
              } catch (e) {
                console.warn('Failed to subscribe to SSE', e);
              }

              // Confirm and save the selection
              const result: ConfirmSelectionResult = await photosPickerService.confirmSelection(session.sessionId);

              // Show warning if photos were skipped due to limit
              if (result.skippedDueToLimit > 0) {
                setLimitWarningInfo({
                  added: result.addedCount,
                  skipped: result.skippedDueToLimit,
                  max: result.maxPhotos,
                });
                setShowLimitWarning(true);
              }

              // Reload fresh data from API
              let freshPhotos: Photo[] = [];
              try {
                const freshData = await photoService.getSelectedPhotos();
                freshPhotos = freshData.photos;
                // Batch these together so React renders photos + clears skeletons atomically
                setPhotos(freshPhotos);
                setSkeletonCount(0);
                setMaxPhotos(freshData.maxPhotos);
                await loadPhotoUrls(freshPhotos);
              } catch (e) {
                console.error('Reload failed, using confirmSelection result', e);
                freshPhotos = result.photos;
                setPhotos(result.photos);
                setSkeletonCount(0);
                setMaxPhotos(result.maxPhotos);
                await loadPhotoUrls(result.photos.filter(p => p.hasThumbnail));
              }

              setIsSaving(false);
              setSavingProgress(null);
              setPickerStatus('idle');

              // Track unprocessed photos and poll until all S3 uploads complete
              const unprocessedCount = freshPhotos.filter(p => !p.hasS3Cache).length;
              if (unprocessedCount > 0) {
                setProcessingTotal(unprocessedCount);
                startProcessingPoll();
              }
            } else {
              // Continue polling
              setTimeout(poll, pollInterval);
            }
          } catch (error) {
            console.error('Polling error', error);
            setPickerStatus('error');
            setIsSaving(false);
            setSavingProgress(null);
            setSkeletonCount(0);
          }
        };

        setTimeout(poll, pollInterval);
      } catch (error) {
        console.error('Failed to open picker', error);
        pickerWindow.close();
        setPickerStatus('error');
      }
    } else {
      // Native: Use in-app browser with polling
      try {
        setPickerStatus('waiting');
        const session = await photosPickerService.createSession();
        const pollInterval = photosPickerService.parsePollInterval(session.pollingConfig.pollInterval);

        // Open picker in in-app browser (don't await - we'll poll for completion)
        openBrowserUrl(session.pickerUri);

        // Poll for completion
        const poll = async () => {
          try {
            const status = await photosPickerService.getSessionStatus(session.sessionId);
            if (status.mediaItemsSet) {
              // Close the browser
              closeBrowser();

              setPickerStatus('complete');
              setIsSaving(true);

              // Get the count of selected photos first
              let photoCount = 0;
              try {
                photoCount = await photosPickerService.getMediaItemsCount(session.sessionId);
                setSavingProgress({current: 0, total: photoCount});
                setSkeletonCount(photoCount);
              } catch (e) {
                console.warn('[GALLERY] native: Failed to get media items count', e);
              }

              // Subscribe to SSE for real-time thumbnail updates
              try {
                const cleanup = await photoService.subscribeToProcessingEvents(
                  handlePhotoProcessed,
                  handleProcessingComplete,
                  (error) => {
                    console.error('SSE error:', error);
                    setSkeletonCount(0);
                    refresh();
                  }
                );
                sseCleanupRef.current = cleanup;
              } catch (e) {
                console.warn('SSE not available (expected on native):', e instanceof Error ? e.message : e);
              }

              // Confirm and save the selection
              const result: ConfirmSelectionResult = await photosPickerService.confirmSelection(session.sessionId);

              // Show warning if photos were skipped due to limit
              if (result.skippedDueToLimit > 0) {
                setLimitWarningInfo({
                  added: result.addedCount,
                  skipped: result.skippedDueToLimit,
                  max: result.maxPhotos,
                });
                setShowLimitWarning(true);
              }

              // Reload fresh data from API
              sseCleanupRef.current?.();
              sseCleanupRef.current = null;
              let freshPhotos: Photo[] = [];
              try {
                const freshData = await photoService.getSelectedPhotos();
                freshPhotos = freshData.photos;
                // Batch these together so React renders photos + clears skeletons atomically
                setPhotos(freshPhotos);
                setSkeletonCount(0);
                setMaxPhotos(freshData.maxPhotos);
                await loadPhotoUrls(freshPhotos);
              } catch (e) {
                console.error('Reload failed:', e);
                freshPhotos = result.photos;
                setPhotos(result.photos);
                setSkeletonCount(0);
                setMaxPhotos(result.maxPhotos);
                await loadPhotoUrls(result.photos.filter(p => p.hasThumbnail));
              }

              setIsSaving(false);
              setSavingProgress(null);
              setPickerStatus('idle');

              // Track unprocessed photos and poll until all S3 uploads complete
              const unprocessedCount = freshPhotos.filter(p => !p.hasS3Cache).length;
              if (unprocessedCount > 0) {
                setProcessingTotal(unprocessedCount);
                startProcessingPoll();
              }
            } else {
              // Continue polling
              setTimeout(poll, pollInterval);
            }
          } catch (error) {
            console.error('Polling error', error);
            closeBrowser();
            setPickerStatus('error');
            setIsSaving(false);
            setSavingProgress(null);
            setSkeletonCount(0);
          }
        };

        setTimeout(poll, pollInterval);
      } catch (error) {
        console.error('Failed to open picker', error);
        setPickerStatus('error');
      }
    }
  };

  const handleDeletePhoto = async () => {
    if (!photoToDelete) return;

    setIsDeleting(true);
    try {
      await photoService.deletePhoto(photoToDelete.id);
      setPhotos(prev => prev.filter(p => p.id !== photoToDelete.id));
      setPhotoUrls(prev => {
        const newUrls = {...prev};
        delete newUrls[photoToDelete.id];
        return newUrls;
      });
      setPhotoToDelete(null);
    } catch (error) {
      console.error('Failed to delete photo', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading && loadError !== 'network') {
    return (
      <SettingsLayout
        currentScreen="gallery"
        onNavigate={navigateToSettingsScreen}
        onLogout={handleLogout}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.text} />
        </View>
      </SettingsLayout>
    );
  }

  // Offline and couldn't load data - show offline message instead of misleading "Connect" CTA
  if (loadError === 'network' && !isPhotosConnected) {
    return (
      <SettingsLayout
        currentScreen="gallery"
        onNavigate={navigateToSettingsScreen}
        onLogout={handleLogout}
      >
        <View style={styles.loadingContainer}>
          <OfflineBanner />
          <BodyText style={{color: theme.textMuted, textAlign: 'center', marginTop: 8}}>{t('common.offlineCannotLoad')}</BodyText>
        </View>
      </SettingsLayout>
    );
  }

  // Not connected state - return early with centered card (like CalendarSettingsScreen)
  if (!isPhotosConnected) {
    return (
      <SettingsLayout
        currentScreen="gallery"
        onNavigate={navigateToSettingsScreen}
        onLogout={handleLogout}
      >
        <ConnectServiceCard
          icon="photo_library"
          title={t('gallery.connectPhotos')}
          description={t('gallery.connectPhotosDescription')}
          buttonLabel={t('gallery.connectGooglePhotos')}
          onConnect={handleConnectPhotos}
          disabled={!isOnline}
        />
      </SettingsLayout>
    );
  }

  const renderPhotosContent = () => {
    return (
      <View style={styles.connectedContainer}>
        {/* Account section */}
        <View style={styles.accountSection}>
          <View style={styles.accountRow}>
            <View style={styles.accountInfo}>
              <Text style={styles.connectedBadge}>{t('gallery.connected')}</Text>
              <Text style={styles.accountEmail}>
                {photosEmail || t('gallery.connectedToPhotos')}
              </Text>
            </View>
            <TouchableOpacity
              style={styles.accountMenuButton}
              onPress={() => setShowAccountMenu(!showAccountMenu)}
            >
              <Text style={styles.menuIcon}>⋮</Text>
            </TouchableOpacity>
          </View>

          {showAccountMenu && (
            <View style={styles.accountMenu}>
              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => {
                  setShowAccountMenu(false);
                  setShowDisconnectConfirm(true);
                }}
              >
                <Text style={styles.menuItemTextDanger}>{t('gallery.disconnect')}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Photo picker section */}
        <View style={styles.pickerSection}>
          <Text style={styles.sectionTitle}>{t('gallery.addPhotos')}</Text>
          <Text style={styles.sectionDescription}>
            {t('gallery.addPhotosDescription', {current: photos.length, max: maxPhotos ?? '-'})}
          </Text>

          {pickerStatus === 'popup_blocked' && (
            <View style={styles.pickerWarning}>
              <Text style={styles.pickerWarningIcon}>⚠️</Text>
              <Text style={styles.pickerWarningText}>{t('gallery.popupBlocked')}</Text>
            </View>
          )}

          <TouchableOpacity
            style={[
              styles.pickerButton,
              (pickerStatus === 'waiting' || isSaving || !isOnline || (maxPhotos !== null && photos.length >= maxPhotos)) && styles.pickerButtonDisabled
            ]}
            onPress={handleOpenPhotoPicker}
            disabled={pickerStatus === 'waiting' || isSaving || !isOnline || (maxPhotos !== null && photos.length >= maxPhotos)}
          >
            {pickerStatus === 'waiting' ? (
              <>
                <ActivityIndicator size="small" color="#fff" style={styles.buttonSpinner} />
                <Text style={styles.pickerButtonText}>{t('gallery.waitingForSelection')}</Text>
              </>
            ) : isSaving ? (
              <>
                <ActivityIndicator size="small" color="#fff" style={styles.buttonSpinner} />
                <Text style={styles.pickerButtonText}>
                  {savingProgress && savingProgress.total > 0
                    ? t('gallery.savingPhotosCount', {count: savingProgress.total})
                    : t('gallery.processingPhotos')}
                </Text>
              </>
            ) : (maxPhotos !== null && photos.length >= maxPhotos) ? (
              <Text style={styles.pickerButtonText}>{t('gallery.limitReached')}</Text>
            ) : (
              <Text style={styles.pickerButtonText}>{t('gallery.openPicker')}</Text>
            )}
          </TouchableOpacity>

          {/* Progress details below button when saving */}
          {isSaving && (
            <View style={styles.progressInfo}>
              <Text style={styles.progressText}>
                {t('gallery.savingDescription')}
              </Text>
            </View>
          )}
        </View>

        {/* Current photos */}
        {(photos.length > 0 || skeletonCount > 0) && (
          <View style={styles.photosSection}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>
                {t('gallery.yourPhotos')} ({photos.length}/{maxPhotos ?? '-'})
              </Text>
              {(() => {
                const totalToProcess = processingTotal || skeletonCount;
                const processedCount = processingTotal > 0
                  ? processingTotal - photos.filter(p => !p.hasS3Cache).length
                  : 0;
                return totalToProcess > 0 ? (
                  <View style={styles.processingBadge}>
                    <ActivityIndicator size="small" color={theme.primary} />
                    <Text style={styles.processingBadgeText}>
                      {processedCount}/{totalToProcess}
                    </Text>
                  </View>
                ) : null;
              })()}
            </View>
            <View style={styles.photosGrid}>
              {photos.map((photo) => {
                const hasThumbnailReady = !!photoUrls[photo.id];
                const isCurrentlyProcessing = (!photo.hasS3Cache && !hasThumbnailReady) || (isSaving && !photo.hasThumbnail && !hasThumbnailReady);
                const isFailed = !isSaving && photo.hasS3Cache && !photo.hasThumbnail && !hasThumbnailReady;
                return (
                  <View key={photo.id} style={styles.photoItem}>
                    <View style={styles.photoImageContainer}>
                      {hasThumbnailReady ? (
                        <Image
                          source={{uri: photoUrls[photo.id]}}
                          style={styles.photoImage}
                        />
                      ) : isCurrentlyProcessing ? (
                        <View style={[styles.photoPlaceholder, styles.photoProcessing]}>
                          <View style={styles.skeletonShimmer}>
                            <ActivityIndicator size="small" color={theme.textMuted} />
                          </View>
                        </View>
                      ) : isFailed ? (
                        <View style={[styles.photoPlaceholder, styles.photoFailed]}>
                          <Text style={styles.failedIcon}>⚠️</Text>
                        </View>
                      ) : (
                        <View style={styles.photoPlaceholder}>
                          <ActivityIndicator size="small" color={theme.textMuted} />
                        </View>
                      )}
                      {!isCurrentlyProcessing && (
                        <TouchableOpacity
                          style={styles.deleteButton}
                          onPress={() => setPhotoToDelete(photo)}
                        >
                          <Text style={styles.deleteButtonText}>×</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                    <Text style={styles.photoName} numberOfLines={1}>
                      {photo.filename || 'Photo'}
                    </Text>
                  </View>
                );
              })}
              {skeletonCount > 0 && Array.from({length: skeletonCount}, (_, i) => (
                <View key={`skeleton-${i}`} style={styles.photoItem}>
                  <View style={styles.photoImageContainer}>
                    <View style={[styles.photoPlaceholder, styles.photoProcessing]}>
                      <View style={styles.skeletonShimmer}>
                        <ActivityIndicator size="small" color={theme.textMuted} />
                      </View>
                    </View>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}
      </View>
    );
  };

  return (
    <SettingsLayout
      currentScreen="gallery"
      onNavigate={navigateToSettingsScreen}
      onLogout={handleLogout}
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          isWide && styles.scrollContentWide,
        ]}
        refreshControl={
          <RefreshControl {...refreshControlProps} />
        }
      >
        <OfflineBanner />
        {renderPhotosContent()}
      </ScrollView>

      {/* Disconnect confirmation modal */}
      <Modal
        visible={showDisconnectConfirm}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDisconnectConfirm(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setShowDisconnectConfirm(false)}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('gallery.confirmDisconnect')}</Text>
            <Text style={styles.modalText}>{t('gallery.confirmDisconnectDescription')}</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setShowDisconnectConfirm(false)}
              >
                <Text style={styles.cancelButtonText}>{t('gallery.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.disconnectButton}
                onPress={handleDisconnect}
              >
                <Text style={styles.disconnectButtonText}>{t('gallery.disconnect')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Pressable>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        visible={photoToDelete !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setPhotoToDelete(null)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => !isDeleting && setPhotoToDelete(null)}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('gallery.confirmDelete')}</Text>
            <Text style={styles.modalText}>{t('gallery.confirmDeleteDescription')}</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setPhotoToDelete(null)}
                disabled={isDeleting}
              >
                <Text style={styles.cancelButtonText}>{t('gallery.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.disconnectButton}
                onPress={handleDeletePhoto}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.disconnectButtonText}>{t('gallery.delete')}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </Pressable>
      </Modal>

      {/* Limit warning modal */}
      <Modal
        visible={showLimitWarning}
        transparent
        animationType="fade"
        onRequestClose={() => setShowLimitWarning(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setShowLimitWarning(false)}
        >
          <View style={styles.modalContent}>
            <Text style={styles.warningIcon}>⚠️</Text>
            <Text style={styles.modalTitle}>{t('gallery.photoLimitTitle')}</Text>
            <Text style={styles.modalText}>
              {t('gallery.photoLimitMessage', {
                added: limitWarningInfo?.added || 0,
                skipped: limitWarningInfo?.skipped || 0,
                max: limitWarningInfo?.max ?? maxPhotos ?? '-',
              })}
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.primaryButton}
                onPress={() => setShowLimitWarning(false)}
              >
                <Text style={styles.primaryButtonText}>{t('gallery.understood')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Pressable>
      </Modal>
    </SettingsLayout>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
  },
  scrollContentWide: {
    paddingHorizontal: 40,
    maxWidth: 800,
    alignSelf: 'center',
    width: '100%',
  },
  connectedContainer: {
    gap: 24,
  },
  accountSection: {
    backgroundColor: theme.surface,
    borderRadius: 12,
    padding: 16,
    position: 'relative',
  },
  accountRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  accountInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  connectedBadge: {
    backgroundColor: 'rgba(34, 197, 94, 0.2)',
    color: '#22c55e',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    fontSize: 12,
    fontWeight: '600',
  },
  accountEmail: {
    color: theme.textMuted,
    fontSize: 14,
    flex: 1,
  },
  accountMenuButton: {
    padding: 8,
  },
  menuIcon: {
    color: theme.textMuted,
    fontSize: 20,
    fontWeight: 'bold',
  },
  accountMenu: {
    position: 'absolute',
    top: 52,
    right: 16,
    backgroundColor: theme.surfaceLight,
    borderRadius: 8,
    padding: 4,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 100,
  },
  menuItem: {
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  menuItemTextDanger: {
    color: '#ef4444',
    fontSize: 14,
  },
  pickerSection: {
    backgroundColor: theme.surface,
    borderRadius: 12,
    padding: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  sectionTitle: {
    color: theme.text,
    fontSize: 16,
    fontWeight: '600',
  },
  sectionDescription: {
    color: theme.textMuted,
    fontSize: 14,
    marginBottom: 16,
  },
  processingBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(238, 140, 43, 0.15)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 6,
  },
  processingBadgeText: {
    color: theme.primary,
    fontSize: 12,
    fontWeight: '600',
  },
  pickerWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(251, 191, 36, 0.15)',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.3)',
  },
  pickerWarningIcon: {
    fontSize: 16,
    marginRight: 8,
  },
  pickerWarningText: {
    color: '#fbbf24',
    fontSize: 14,
    flex: 1,
  },
  pickerButton: {
    backgroundColor: theme.primary,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pickerButtonDisabled: {
    backgroundColor: theme.surfaceLight,
  },
  pickerButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  buttonSpinner: {
    marginRight: 8,
  },
  progressInfo: {
    marginTop: 12,
    padding: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
  },
  progressText: {
    color: theme.textMuted,
    fontSize: 13,
    textAlign: 'center',
    lineHeight: 18,
  },
  photosSection: {
    backgroundColor: theme.surface,
    borderRadius: 12,
    padding: 20,
  },
  photosGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 12,
  },
  photoItem: {
    width: 100,
    alignItems: 'center',
  },
  photoImageContainer: {
    position: 'relative',
    width: 100,
    height: 100,
  },
  photoImage: {
    width: 100,
    height: 100,
    borderRadius: 8,
    backgroundColor: theme.surfaceLight,
  },
  photoPlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 8,
    backgroundColor: theme.surfaceLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoProcessing: {
    backgroundColor: 'rgba(238, 140, 43, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(238, 140, 43, 0.3)',
    borderStyle: 'dashed',
  },
  photoFailed: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.3)',
  },
  failedIcon: {
    fontSize: 24,
  },
  skeletonShimmer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteButton: {
    position: 'absolute',
    top: -8,
    right: -8,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#ef4444',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 4,
  },
  deleteButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    lineHeight: 20,
  },
  photoName: {
    color: theme.textMuted,
    fontSize: 12,
    marginTop: 4,
    textAlign: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: theme.surface,
    borderRadius: 16,
    padding: 24,
    width: '90%',
    maxWidth: 400,
    alignItems: 'center',
  },
  warningIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  modalTitle: {
    color: theme.text,
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 12,
    textAlign: 'center',
  },
  modalText: {
    color: theme.textMuted,
    fontSize: 14,
    marginBottom: 24,
    lineHeight: 20,
    textAlign: 'center',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
    width: '100%',
  },
  cancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: theme.surfaceLight,
  },
  cancelButtonText: {
    color: theme.text,
    fontSize: 14,
    fontWeight: '500',
  },
  disconnectButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: '#ef4444',
    minWidth: 80,
    alignItems: 'center',
  },
  disconnectButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
  primaryButton: {
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 8,
    backgroundColor: theme.primary,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
});
